import bmiReference from './cdc-bmi-age.json'

// Screening labels, never diagnoses. Sources and age limits: REFERENCES.md.
const badge = (label, tone, detail) => ({ label, tone, detail })
const knownAge = months => Number.isInteger(months) && months >= 0
const unknownAge = () => badge('Âge requis', 'neutral', 'Renseignez la date de naissance ou l’âge du patient.')
const obesity = (bmi, second = 35, third = 40) => bmi >= third ? 'Obésité classe III' : bmi >= second ? 'Obésité classe II' : 'Obésité classe I'

function imc(bmi, { age_mois: months, sexe } = {}, pregnant = false) {
    if (!Number.isFinite(bmi) || bmi <= 0) return null
    if (pregnant) return badge('IMC prégestationnel requis', 'neutral', 'Pendant la grossesse, le poids actuel ne permet pas cette classification.')
    if (!knownAge(months)) return unknownAge()
    if (months < 24) return badge('Courbe poids / taille requise', 'neutral', 'Avant 2 ans, ne pas appliquer les catégories d’IMC adulte.')
    let low = 18.5, high = 25, obese = 30, second = 35, third = 40
    let detail = 'Référence CDC adulte (20 ans et plus), hors grossesse. Repère de dépistage, pas un diagnostic.'
    if (months < 240) {
        if (!['M', 'F'].includes(sexe)) return badge('Sexe requis', 'neutral', 'La référence d’IMC de 2 à 19 ans dépend de l’âge et du sexe.')
        ;[low, high, obese] = bmiReference[sexe][months - 24]
        second = Math.min(1.2 * obese, 35); third = Math.min(1.4 * obese, 40)
        detail = 'Référence CDC selon l’âge et le sexe (2–19 ans) ; classes d’obésité AAP. Repère de dépistage.'
    }
    if (bmi < low) return badge('Maigreur', 'amber', detail)
    if (bmi < high) return badge('Corpulence normale', 'green', detail)
    if (bmi < obese) return badge('Surpoids', 'amber', detail)
    const category = obesity(bmi, second, third)
    return badge(months < 240 ? category.replace('Obésité', 'Obésité pédiatrique') : category, 'amber', detail)
}

function temperature(n, { age_mois: months } = {}) {
    const detail = 'Repères indicatifs en °C ; le site de mesure et le contexte clinique peuvent modifier l’interprétation.'
    if (n < 35) return badge('Hypothermie · alerte', 'red', detail + ' Évaluation clinique immédiate.')
    if (n >= 38 && knownAge(months) && (months < 3 || (months < 6 && n >= 39))) return badge('Température élevée du nourrisson · alerte', 'red', detail + ' Évaluation médicale urgente.')
    if (n < 36.5) return badge('Température basse', 'amber', detail)
    if (n > 37.8) return badge('Température élevée', 'amber', detail)
    return badge('Température normale', 'green', detail)
}

function pouls(n, { age_mois: months } = {}) {
    if (!knownAge(months)) return unknownAge()
    const range = months < 1 ? [70, 190] : months < 12 ? [80, 160] : months < 36 ? [80, 130] : months < 60 ? [80, 120] : months < 84 ? [75, 115] : months < 120 ? [70, 110] : [60, 100]
    const detail = `Repère au repos selon l’âge : ${range[0]}–${range[1]} BPM (MedlinePlus). Sport, sommeil, médicaments et symptômes à prendre en compte.`
    if (n < range[0]) return badge('Pouls lent · bradycardie possible', 'amber', detail)
    if (n > range[1]) return badge('Pouls rapide · tachycardie possible', 'amber', detail)
    return badge('Pouls normal au repos', 'green', detail)
}

function saturation(n) {
    const detail = 'Repère général : 95–100 %. Cible à individualiser (pathologie respiratoire, oxygène, altitude, âge néonatal). Vérifiez le signal de l’oxymètre.'
    if (n <= 88) return badge('Saturation très basse · alerte', 'red', detail + ' Évaluation clinique immédiate.')
    if (n <= 92) return badge('Saturation basse · avis médical', 'red', detail)
    if (n < 95) return badge('Saturation diminuée', 'amber', detail)
    return badge('Saturation dans les repères', 'green', detail)
}

// RCH / AAP simplified SCREENING thresholds, not the diagnostic age/height percentiles.
const bpScreen = {
    M: [[98,52],[100,55],[101,58],[102,60],[103,63],[105,66],[106,68],[107,69],[107,70],[108,72],[110,74],[113,75]],
    F: [[98,54],[101,58],[102,60],[103,62],[104,64],[105,67],[106,68],[107,69],[108,71],[109,72],[111,74],[114,75]]
}
function tension([systolic, diastolic], { age_mois: months, sexe } = {}, pregnant = false) {
    if (pregnant) {
        const detail = 'Repères NICE grossesse : évaluation obstétricale, sans diagnostic sur une mesure isolée.'
        if (systolic >= 160 || diastolic >= 110) return badge('TA très élevée · alerte obstétricale', 'red', detail + ' Évaluation médicale urgente.')
        if (systolic >= 140 || diastolic >= 90) return badge('TA élevée · avis obstétrical', 'amber', detail)
        return badge('Référence obstétricale requise', 'neutral', detail)
    }
    if (!knownAge(months)) return unknownAge()
    if (months < 12) return badge('Référence nourrisson requise', 'neutral', 'La tension du nourrisson nécessite une référence spécifique ; la PAM reste calculée.')
    if (months < 156) {
        if (!bpScreen[sexe]) return badge('Sexe requis', 'neutral', 'Le repère de dépistage pédiatrique dépend de l’âge et du sexe.')
        const [s, d] = bpScreen[sexe][Math.floor(months / 12) - 1]
        const high = systolic >= s || diastolic >= d
        return badge(high ? 'TA élevée au dépistage · à vérifier' : 'Seuil haut de dépistage non atteint', high ? 'amber' : 'neutral', `Repère RCH/AAP : ${s}/${d} mmHg. Ne permet pas de conclure à une TA normale ni d’exclure une hypotension. Confirmer selon âge, sexe, taille et brassard.`)
    }
    const detail = 'Repères AHA / AAP (dès 13 ans), hors grossesse. Une mesure isolée ne diagnostique pas une hypertension ; confirmer au repos.'
    if (systolic > 180 || diastolic > 120) return badge('TA très élevée · alerte', 'red', detail + ' Évaluation clinique immédiate, notamment en présence de symptômes.')
    if (systolic >= 140 || diastolic >= 90) return badge('TA élevée · seuil stade 2', 'amber', detail)
    if (systolic >= 130 || diastolic >= 80) return badge('TA élevée · seuil stade 1', 'amber', detail)
    if (systolic < 90 || diastolic < 60) return badge('TA basse · à vérifier', 'amber', detail)
    if (systolic >= 120) return badge('TA légèrement élevée', 'amber', detail)
    return badge('TA normale', 'green', detail)
}

export default { imc, temperature, pouls, saturation, tension }
